import { FileTextIcon, PackageIcon, RepeatIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { useAdmin } from '../AdminContext';
import { btnGhost, listBox } from '../ui/styles';
import { orderTotal } from '../../utils/account';
import { printInvoice } from '../../utils/invoice';
import { euro } from '../../core/format/currency';
import { dayLong, dayMedium, newestFirst } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import type { AccountOrder } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';
const statusTone: Record<AccountOrder['status'], 'accent' | 'soft' | 'neutral'> = {
  'In behandeling': 'accent',
  Onderweg: 'soft',
  Bezorgd: 'neutral'
};

/** Bestellingen van één klant, met de factuur zoals zij die ook kan downloaden. */
export function CustomerOrders({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const orders = newestFirst(account.orders);
  const spent = orders.reduce((s, o) => s + orderTotal(o), 0);
  if (orders.length === 0) {
    return <EmptyState icon={PackageIcon} title={`${account.customer.firstName} heeft nog niets besteld`} text="Salonbezoeken worden in de salon afgerekend en staan hier niet bij." data-id="element-1196" data-mp="2urtf0hs82e2s" />;
  }
  const openInvoice = async (o: AccountOrder) => {
    if (!(await printInvoice(o, account.customer, site.salon, index.productById, dayLong(o.date)))) toast.error('De factuur kon niet openen', {
      description: 'Probeer het nog eens.'
    });
  };
  return <div data-id="element-1197" data-mp="2bv1xodw8q91d">
      <p className="mb-4 text-[14px] text-muted" data-id="element-1198" data-mp="2bvabc806tidm">
        {plural(orders.length, 'bestelling', 'bestellingen')} · samen {euro(spent, {
        cents: true
      })}
      </p>
      <ul className={listBox} data-id="element-1199" data-mp="a2vtta1k2fxq">
        {orders.map(o => <li key={o.id} className="px-4 py-4 sm:px-5" data-id="element-1200" data-mp="f174fmzocuh0">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-1201" data-mp="ezvyphmo5cn9">
              <Badge tone={statusTone[o.status]} data-id="element-1202" data-mp="dk9rd4ui8iur">{o.status}</Badge>
              <span className="min-w-0 flex-1" data-id="element-1203" data-mp="8jvw6ne053e5">
                <span className="block text-[14px] text-ink" data-id="element-1204" data-mp="8j3x32l74nj4">
                  {o.id}
                  {o.viaSubscription && <span className="ml-2 inline-flex items-center gap-1 align-middle text-[12px] text-clay" data-id="element-1205" data-mp="3urn49z603s3m">
                      <RepeatIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-1206" data-mp="w9q3kpfjxzr0" />
                      Abonnement
                    </span>}
                </span>
                <span className="block text-[13px] text-muted" data-id="element-1207" data-mp="mjwv3xapi0qz">{dayMedium(o.date)}</span>
              </span>
              <span className="text-[14px] tabular-nums text-ink" data-id="element-1208" data-mp="hlmz5kh5qw4b">{euro(orderTotal(o), {
              cents: true
            })}</span>
              <button type="button" onClick={() => openInvoice(o)} className={btnGhost} aria-label={`Factuur van ${o.id}`} data-id="element-1209" data-mp="1f0r25ccrfta1">
                <FileTextIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1210" data-mp="1a87qikxmv87v" />
                Factuur
              </button>
            </div>
            <p className="mt-2 truncate text-[13px] text-muted" data-id="element-1211" data-mp="1mjqp1rzfk2y5">
              {o.lines.map(l => `${l.qty} × ${index.productById(l.productId)?.name ?? l.productId}`).join(' · ')}
            </p>
          </li>)}
      </ul>
    </div>;
}